import { useQueryClient } from 'react-query'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { useToast } from '@wartek-id/toast-v2'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import {
  API_GET_KINERJA_HARIAN_LOGS_QUERY_KEY,
  useAPIGetKinerjaHarianContents,
  useAPIGetKinerjaHarianLogs,
  useAPIPostUpsertKinerjaHarian,
} from 'api/kinerja/kinerja-harian-ps'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import { EDIT_DELETE_DISABLED_STATES } from 'app/KinerjaHarian/constants'

import type { RequestUpsertKinerjaHarian } from 'types/kinerja-harian'

const basePath = `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/harian`

export default function useKinerjaHarianPS(entryDate?: string) {
  const { periodYear } = usePeriodContext()
  const toast = useToast()
  const queryClient = useQueryClient()
  const router = useRouter()

  const { tipe } = useKinerjaTipe()

  const { data: { data: contents = [] } = {}, isLoading: isContentLoading } =
    useAPIGetKinerjaHarianContents({
      params: {
        role: 'PS',
        tipe,
        requestedDate: `${periodYear}-01-01`,
      },
      options: {
        useErrorBoundary: false,
        staleTime: 60 * 60 * 1000, // 60 minutes
        enabled: Boolean(tipe),
      },
    })

  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: basePath,
  })

  const { data: logsToEdit, isFetched: isLogsFetched } =
    useAPIGetKinerjaHarianLogs({
      params: {
        startDate: entryDate,
        endDate: entryDate,
      },
      options: {
        enabled: Boolean(entryDate),
      },
    })

  const [doneCheckingLogs, setDoneCheckingLogs] = useState(!entryDate)

  // Wait for the logs entries
  useEffect(() => {
    if (entryDate && isLogsFetched) {
      if (!logsToEdit?.data || logsToEdit.data.length === 0) {
        // Redirect to list page if no logs are found
        router.replace(basePath)
      }
      // Redirect if log state is not editable (sent to or processed by BKN)
      else if (
        EDIT_DELETE_DISABLED_STATES.includes(logsToEdit?.data?.[0]?.state)
      ) {
        // Redirect to list page
        handleBack()
      } else {
        setDoneCheckingLogs(true)
      }
    }
  }, [isLogsFetched, logsToEdit, entryDate, router, handleBack])

  const { mutateAsync, isLoading: isSubmitting } =
    useAPIPostUpsertKinerjaHarian()

  const onUpsert = async (payload: RequestUpsertKinerjaHarian) => {
    try {
      await mutateAsync(payload)

      toast({
        message: 'Berhasil simpan catatan kinerja!',
        type: 'success',
      })

      queryClient.invalidateQueries(API_GET_KINERJA_HARIAN_LOGS_QUERY_KEY)

      const month = payload.entriesDate[0]?.split('-')[1]

      router.replace({
        pathname: basePath,
        query: {
          month: month ? Number(month) : undefined,
          saved: 'true',
        },
      })
    } catch (error) {
      toast({ message: 'Gagal simpan catatan kinerja. Coba lagi.' })

      console.error('Kinerja Harian submission error:', error)
    }
  }

  const isEditModeLoading = !!entryDate && !doneCheckingLogs

  return {
    basePath,
    periodYear,

    isLoading: isContentLoading || isEditModeLoading,
    contents,
    logsToEdit: logsToEdit?.data,

    isSubmitting,
    onUpsert,
  }
}
